/**
 * PortalRegisterParent.js
 * Registration page for parent profile when multiple minors share the same email.
 * Allows creating a parent user in anagrafica with role "parent" (without athlete role)
 * and automatically links minor athletes to this parent profile.
 */

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Card, Form, Button, Alert, Spinner, Container, Row, Col, InputGroup } from 'react-bootstrap';
import { portalService } from '../../services/api';
import './Portal.css';

const PortalRegisterParent = () => {
  const { token } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [tokenValid, setTokenValid] = useState(false);
  const [tokenError, setTokenError] = useState('');
  const [athleteData, setAthleteData] = useState(null);

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    fiscalCode: '',
    phoneNumber: '',
    password: '',
    confirmPassword: ''
  });

  const [showPassword, setShowPassword] = useState(false);
  const [formError, setFormError] = useState('');

  useEffect(() => {
    const validateToken = async () => {
      if (!token) {
        setTokenError('Token non fornito nel link di accesso.');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const result = await portalService.validateToken(token);

        if (result.success && result.data?.valid) {
          const data = result.data;
          setAthleteData(data.athlete);
          setTokenValid(true);

          const emailToUse =
            data.existingParentAccount?.email ||
            data.athlete?.defaultLoginEmail ||
            data.athlete?.email ||
            '';

          setFormData(prev => ({
            ...prev,
            email: emailToUse
          }));
        } else {
          setTokenValid(false);
          setTokenError(result.message || 'Link di accesso non valido o scaduto.');
        }
      } catch (err) {
        console.error('Error validating token for parent registration:', err);
        setTokenValid(false);
        setTokenError('Errore durante la verifica del link di accesso.');
      } finally {
        setLoading(false);
      }
    };

    validateToken();
  }, [token]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'fiscalCode' ? value.toUpperCase() : value
    }));
    if (formError) setFormError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.firstName.trim()) {
      setFormError('Il nome è obbligatorio');
      return;
    }

    if (!formData.lastName.trim()) {
      setFormError('Il cognome è obbligatorio');
      return;
    }

    if (!formData.fiscalCode.trim()) {
      setFormError('Il codice fiscale è obbligatorio');
      return;
    }

    if (formData.fiscalCode.trim().length !== 16) {
      setFormError('Il codice fiscale deve essere di 16 caratteri');
      return;
    }

    if (!formData.phoneNumber.trim()) {
      setFormError('Il numero di telefono è obbligatorio');
      return;
    }

    if (!formData.password) {
      setFormError('La password è obbligatoria');
      return;
    }

    if (formData.password.length < 6) {
      setFormError('La password deve essere di almeno 6 caratteri');
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setFormError('Le password non coincidono');
      return;
    }

    try {
      setSubmitLoading(true);
      setFormError('');

      const result = await portalService.registerParent(
        token,
        formData.firstName.trim(),
        formData.lastName.trim(),
        formData.fiscalCode.trim(),
        formData.phoneNumber.trim(),
        formData.password
      );

      if (result.success) {
        navigate('/portal/profile');
        window.location.reload();
      } else {
        setFormError(result.message || 'Errore durante la registrazione del genitore');
      }
    } catch (err) {
      console.error('Error registering parent profile:', err);
      setFormError('Errore di connessione al server');
    } finally {
      setSubmitLoading(false);
    }
  };

  if (loading) {
    return (
      <Container className="py-5 text-center">
        <div className="py-5">
          <Spinner animation="border" variant="primary" style={{ width: '3rem', height: '3rem' }} />
          <h4 className="mt-3 text-muted">Verifica link di accesso in corso...</h4>
        </div>
      </Container>
    );
  }

  if (!tokenValid) {
    return (
      <Container className="py-5">
        <Row className="justify-content-center">
          <Col md={8} lg={6}>
            <Card className="shadow border-0 text-center p-4">
              <Card.Body>
                <div className="text-danger mb-3">
                  <i className="fas fa-exclamation-circle fa-4x"></i>
                </div>
                <h3 className="mb-3">Link non valido o scaduto</h3>
                <Alert variant="danger" className="text-start">
                  {tokenError}
                </Alert>
                <p className="text-muted">
                  I link di accesso hanno una validità limitata. Contatta la segreteria o l'amministratore per richiederne uno nuovo.
                </p>
                <div className="mt-4">
                  <Link to="/login" className="btn btn-primary">
                    <i className="fas fa-sign-in-alt me-2"></i>
                    Vai alla pagina di Login
                  </Link>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    );
  }

  return (
    <Container className="py-5">
      <Row className="justify-content-center">
        <Col md={9} lg={7} xl={6}>
          <Card className="shadow-lg border-0">
            <Card.Header className="bg-primary text-white text-center py-4">
              <h3 className="mb-1">
                <i className="fas fa-user-shield me-2"></i>
                Registrazione Profilo Genitore
              </h3>
              <p className="mb-0 text-white-50">Gestione account atleti minorenni</p>
            </Card.Header>
            <Card.Body className="p-4 p-md-5">
              <Alert variant="info" className="d-flex align-items-start mb-4" data-testid="parent-info-alert">
                <i className="fas fa-info-circle fs-4 me-3 text-info mt-1"></i>
                <div>
                  <h6 className="alert-heading mb-1 fw-bold">Creazione Account Genitore/Tutore</h6>
                  <p className="mb-0 small">
                    Stai creando l'account genitore per gestire l'accesso al portale per {athleteData?.firstName ? <strong>{athleteData.firstName} {athleteData.lastName}</strong> : 'i tuoi figli'}. Una volta registrato, potrai visualizzare e gestire tutti i profili dei tuoi figli con un unico accesso.
                  </p>
                </div>
              </Alert>

              {formError && (
                <Alert variant="danger" onClose={() => setFormError('')} dismissible data-testid="register-parent-error">
                  {formError}
                </Alert>
              )}

              <Form onSubmit={handleSubmit} data-testid="register-parent-form">
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3" controlId="parent-first-name">
                      <Form.Label className="fw-bold">Nome *</Form.Label>
                      <Form.Control
                        type="text"
                        name="firstName"
                        value={formData.firstName}
                        onChange={handleInputChange}
                        placeholder="Nome genitore"
                        required
                        disabled={submitLoading}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3" controlId="parent-last-name">
                      <Form.Label className="fw-bold">Cognome *</Form.Label>
                      <Form.Control
                        type="text"
                        name="lastName"
                        value={formData.lastName}
                        onChange={handleInputChange}
                        placeholder="Cognome genitore"
                        required
                        disabled={submitLoading}
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <Form.Group className="mb-3" controlId="parent-email">
                  <Form.Label className="fw-bold">Email di Accesso (Login) *</Form.Label>
                  <Form.Control
                    type="email"
                    name="email"
                    value={formData.email}
                    disabled
                  />
                  <Form.Text className="text-muted">
                    Questa email corrisponde a quella associata al profilo dei figli ed è l'email che userai per accedere.
                  </Form.Text>
                </Form.Group>

                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3" controlId="parent-fiscal-code">
                      <Form.Label className="fw-bold">Codice Fiscale *</Form.Label>
                      <Form.Control
                        type="text"
                        name="fiscalCode"
                        value={formData.fiscalCode}
                        onChange={handleInputChange}
                        placeholder="16 caratteri"
                        maxLength={16}
                        required
                        disabled={submitLoading}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3" controlId="parent-phone">
                      <Form.Label className="fw-bold">Telefono *</Form.Label>
                      <Form.Control
                        type="tel"
                        name="phoneNumber"
                        value={formData.phoneNumber}
                        onChange={handleInputChange}
                        placeholder="Es. 3331234567"
                        required
                        disabled={submitLoading}
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <Form.Group className="mb-3" controlId="parent-password">
                  <Form.Label className="fw-bold">Crea Password *</Form.Label>
                  <InputGroup>
                    <Form.Control
                      type={showPassword ? 'text' : 'password'}
                      name="password"
                      value={formData.password}
                      onChange={handleInputChange}
                      placeholder="Almeno 6 caratteri"
                      minLength={6}
                      required
                      disabled={submitLoading}
                    />
                    <Button
                      variant="outline-secondary"
                      onClick={() => setShowPassword(!showPassword)}
                      type="button"
                    >
                      <i className={`fas ${showPassword ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                    </Button>
                  </InputGroup>
                </Form.Group>

                <Form.Group className="mb-4" controlId="parent-confirm-password">
                  <Form.Label className="fw-bold">Conferma Password *</Form.Label>
                  <Form.Control
                    type={showPassword ? 'text' : 'password'}
                    name="confirmPassword"
                    value={formData.confirmPassword}
                    onChange={handleInputChange}
                    placeholder="Ripeti la password"
                    minLength={6}
                    required
                    disabled={submitLoading}
                  />
                </Form.Group>

                <Button
                  type="submit"
                  variant="primary"
                  size="lg"
                  className="w-100 py-3 fw-bold"
                  disabled={submitLoading}
                  data-testid="submit-register-parent-btn"
                >
                  {submitLoading ? (
                    <>
                      <Spinner animation="border" size="sm" className="me-2" />
                      Registrazione in corso...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-check-circle me-2"></i>
                      Registra Profilo Genitore e Accedi
                    </>
                  )}
                </Button>
              </Form>

              <div className="text-center mt-4">
                <Link to={`/portal/setup/${token}`} className="text-decoration-none small text-muted">
                  <i className="fas fa-arrow-left me-1"></i>
                  Torna all'attivazione dell'atleta
                </Link>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default PortalRegisterParent;
